import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { copyText } from '@/lib/copyText';
import { cn } from '@/lib/utils';

/**
 * «Скопировать» у ответа ученика в разборе урока (куратор и админ, 13.10.2026). Копирует ответ целиком одним
 * нажатием, после — «Скопировано» на две секунды. Кусок текста сочинения выделяется и обычным способом
 * (`.review-inert [data-essay-answer]` в index.css).
 *
 * Кнопка живёт в ряду плашек НАД `.review-inert`, а не внутри: там погашены события, и нажать её было бы нельзя.
 */
export function ReviewCopyButton({ text }: { text: string }) {
  const { t } = useTranslation();
  const [state, setState] = useState<'idle' | 'done' | 'failed'>('idle');
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = () => {
    // Без await до записи: буфер пускает только под живым жестом (lib/copyText.ts).
    void copyText(text).then((ok) => {
      setState(ok ? 'done' : 'failed');
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setState('idle'), 2000);
    });
  };

  return (
    <button
      type="button"
      data-review-copy
      onClick={copy}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-3 py-1 text-xs font-semibold transition-colors',
        state === 'done'
          ? 'bg-success-container text-on-success-container'
          : 'bg-surface-container-highest text-on-surface-variant hover:text-on-surface',
      )}
    >
      <span className="material-symbols-outlined text-sm">{state === 'done' ? 'check' : 'content_copy'}</span>
      <span aria-live="polite">
        {state === 'done' ? t('blockReview.copied') : state === 'failed' ? t('blockReview.copyFailed') : t('blockReview.copy')}
      </span>
    </button>
  );
}
